import  { useState } from 'react';
import { Star, Clock, Users, Award, ChevronRight, Calendar, Phone, CheckCircle, Zap, Heart, Droplets, Thermometer, Activity, Shield } from 'lucide-react';
import { SEO } from '../../SEO';

const WellnessTreatments = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  const categories = [
    { id: 'all', name: 'All Treatments', icon: '🌟' },
    { id: 'advanced-therapy', name: 'Advanced Therapy', icon: '⚡' },
    { id: 'rejuvenation', name: 'Rejuvenation', icon: '✨' },
    { id: 'detox', name: 'Detox & Cleanse', icon: '🌿' },
    { id: 'pain-relief', name: 'Pain Relief', icon: '💆' },
    { id: 'wellness-boost', name: 'Wellness Boost', icon: '🚀' }
  ];

  const treatments = [
    {
      id: 1,
      name: 'IV Nutrient Therapy',
      category: 'advanced-therapy',
      price: 4999,
      duration: '45-60 mins',
      icon: <Droplets className="w-6 h-6" />,
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=300&fit=crop',
      badge: 'Most Popular',
      description: 'Instant hydration, vitamins, and detox through luxurious drip session tailored to your body\'s needs.',
      benefits: ['Instant energy boost', 'Enhanced immunity', 'Faster recovery', 'Better hydration'],
      rating: 4.9,
      reviews: 156,
      sessions: 1,
      suitable: 'Adults seeking energy boost and wellness optimization'
    },
    {
      id: 2,
      name: 'Cryotherapy Chamber',
      category: 'advanced-therapy',
      price: 2999,
      duration: '3-5 mins',
      icon: <Thermometer className="w-6 h-6" />,
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop',
      badge: 'Trending',
      description: 'Step into a -110°C chamber for fat burning, recovery, and a mood-boosting endorphin surge.',
      benefits: ['Accelerated recovery', 'Fat burning', 'Mood enhancement', 'Reduced inflammation'],
      rating: 4.8,
      reviews: 134,
      sessions: 1,
      suitable: 'Athletes and fitness enthusiasts'
    },
    {
      id: 3,
      name: 'Hyperbaric Oxygen Therapy',
      category: 'rejuvenation',
      price: 5999,
      duration: '60-90 mins',
      icon: <Activity className="w-6 h-6" />,
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=300&fit=crop',
      badge: 'Premium',
      description: 'Breathe pure oxygen in a pressurized chamber to enhance cellular healing, anti-aging, and detox.',
      benefits: ['Enhanced healing', 'Anti-aging effects', 'Better circulation', 'Toxin elimination'],
      rating: 4.7,
      reviews: 89,
      sessions: 1,
      suitable: 'Individuals seeking anti-aging and cellular repair'
    },
    {
      id: 4,
      name: 'Infrared Sauna Therapy',
      category: 'detox',
      price: 1999,
      duration: '30-45 mins',
      icon: <Zap className="w-6 h-6" />,
      image: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400&h=300&fit=crop',
      badge: 'Relaxing',
      description: 'Deep heat therapy to release toxins, improve circulation, and melt stress away luxuriously.',
      benefits: ['Deep detoxification', 'Stress relief', 'Improved circulation', 'Pain reduction'],
      rating: 4.6,
      reviews: 203,
      sessions: 1,
      suitable: 'Anyone seeking relaxation and detox'
    },
    {
      id: 5,
      name: 'Laser Therapy Treatment',
      category: 'pain-relief',
      price: 3999,
      duration: '30-45 mins',
      icon: <Zap className="w-6 h-6" />,
      image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=400&h=300&fit=crop',
      badge: 'Advanced',
      description: 'Non-invasive advanced laser treatments for skin rejuvenation, pain relief and cellular healing.',
      benefits: ['Pain relief', 'Skin rejuvenation', 'Cellular repair', 'Non-invasive healing'],
      rating: 4.8,
      reviews: 167,
      sessions: 1,
      suitable: 'Those with chronic pain or skin concerns'
    },
    {
      id: 6,
      name: 'Stem Cell Therapy',
      category: 'rejuvenation',
      price: 15999,
      duration: '90-120 mins',
      icon: <Heart className="w-6 h-6" />,
      image: 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?w=400&h=300&fit=crop',
      badge: 'Revolutionary',
      description: 'Regenerative science meets natural healing to slow aging and rejuvenate the body from within.',
      benefits: ['Anti-aging', 'Tissue regeneration', 'Enhanced recovery', 'Longevity boost'],
      rating: 4.9,
      reviews: 45,
      sessions: 1,
      suitable: 'Individuals seeking advanced anti-aging solutions'
    }
  ];

  const filteredTreatments = selectedCategory === 'all' 
    ? treatments 
    : treatments.filter(treatment => treatment.category === selectedCategory);

  const TreatmentCard = ({ treatment }:any) => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
      <div className="relative">
        <img 
          src={treatment.image} 
          alt={treatment.name}
          className="w-full h-48 object-cover"
        />
        {treatment.badge && (
          <span className="absolute top-4 left-4 bg-teal-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            {treatment.badge}
          </span>
        )}
        <div className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-md">
          {treatment.icon}
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{treatment.name}</h3>
        <p className="text-gray-600 mb-4 line-clamp-2">{treatment.description}</p>
        
        <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{treatment.duration}</span>
          </div>
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span>{treatment.rating}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            <span>{treatment.reviews} reviews</span>
          </div>
        </div>
        
        <div className="mb-4">
          <h4 className="font-semibold text-gray-800 mb-2">Key Benefits:</h4>
          <div className="grid grid-cols-2 gap-2">
            {treatment.benefits.slice(0, 4).map((benefit:any, index:any) => (
              <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-3 h-3 text-green-500" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="text-2xl font-bold text-teal-600">₹{treatment.price}</div>
          <button 
            onClick={() => setSelectedTreatment(treatment)}
            className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
          >
            Book Now
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const TreatmentModal = ({ treatment, onClose }:any) => {
    if (!treatment) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="relative">
            <img 
              src={treatment.image} 
              alt={treatment.name}
              className="w-full h-64 object-cover rounded-t-xl"
            />
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100"
            >
              ✕
            </button>
          </div>
          
          <div className="p-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{treatment.name}</h2>
            <p className="text-gray-600 mb-6">{treatment.description}</p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="text-center p-4 bg-teal-50 rounded-lg">
                <Clock className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <div className="font-semibold">{treatment.duration}</div>
                <div className="text-sm text-gray-600">Duration</div>
              </div>
              <div className="text-center p-4 bg-teal-50 rounded-lg">
                <Star className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <div className="font-semibold">{treatment.rating}/5</div>
                <div className="text-sm text-gray-600">{treatment.reviews} Reviews</div>
              </div>
              <div className="text-center p-4 bg-teal-50 rounded-lg">
                <Shield className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                <div className="font-semibold">₹{treatment.price}</div>
                <div className="text-sm text-gray-600">Per Session</div>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-4">Complete Benefits:</h3>
              <div className="grid md:grid-cols-2 gap-3">
                {treatment.benefits.map((benefit:any, index:any) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Suitable For:</h4>
              <p className="text-blue-700">{treatment.suitable}</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="flex-1 bg-teal-600 text-white py-3 px-6 rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-2">
                <Calendar className="w-5 h-5" />
                Book Appointment
              </button>
              <button className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
                <Phone className="w-5 h-5" />
                Call for Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Therapeutic Wellness Treatments & Advanced Therapies | MetaFit Wellness"
        description="Experience advanced therapeutic wellness treatments including IV nutrient therapy, cryotherapy, infrared sauna, and hyperbaric oxygen therapy at MetaFit Wellness."
        canonical="/therapeutic-wellness-treatment"
        keywords="wellness treatments, therapeutic therapies, IV nutrient therapy, cryotherapy, infrared sauna, hyperbaric oxygen, MetaFit Wellness"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
                Wellness
              <span className="block text-teal-200">Treatments</span>
            </h1>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              Elevate your well-being with our premium range of cutting-edge wellness therapies—combining science, luxury, and holistic healing.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                <span>Certified Therapists</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span>Advanced Technology</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                <span>Premium Experience</span>
              </div>
            </div>
          </div>
        </div>
      </div>

   

      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center py-20">
        <p className="text-2xl font-semibold text-green-600">Coming Soon</p>
      </div>

      {/* SEO Content Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-teal-50 rounded-3xl mb-16">
        <div className="max-w-6xl mx-auto px-6">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-blue-100 to-teal-100 px-4 py-2 rounded-full mb-4">
              <span className="text-blue-700 font-semibold text-sm">PREMIUM WELLNESS THERAPIES</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-700 to-teal-600 bg-clip-text text-transparent mb-6">
              Experience Luxury Wellness Treatments
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover cutting-edge therapeutic solutions combining advanced technology with holistic wellness principles
            </p>
          </div>

          {/* Main Content Card */}
          <div className="bg-white rounded-2xl shadow-lg p-10 mb-12 border-l-4 border-blue-500 hover:shadow-xl transition-all duration-300">
            <div className="flex gap-6">
              <div className="text-5xl flex-shrink-0">✨</div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">The Future of Wellness Technology</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  At Metafit Wellness, we offer ultra-luxury wellness treatments that merge cutting-edge scientific innovation with therapeutic excellence. Our comprehensive range of advanced therapies addresses everything from cellular regeneration and anti-aging to pain management and athletic recovery. Each treatment utilizes state-of-the-art technology and evidence-based methodologies delivered by certified wellness practitioners in luxurious, serene settings designed for optimal healing and rejuvenation.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you're an athlete seeking accelerated recovery, someone pursuing advanced anti-aging solutions, or looking for innovative pain management options, our premium treatments provide scientifically proven results with immediate and long-lasting benefits.
                </p>
              </div>
            </div>
          </div>

          {/* Three Column Grid */}
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            
            {/* Column 1: Cutting-Edge Technology */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mb-5 text-white text-2xl">
                ⚡
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Advanced Technology</h3>
              <ul className="space-y-3">
                <li className="flex gap-3 text-gray-700">
                  <span className="text-blue-600 font-bold text-lg">•</span>
                  <span>Cryotherapy for accelerated recovery and inflammation reduction</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-blue-600 font-bold text-lg">•</span>
                  <span>Hyperbaric oxygen therapy for cellular healing and anti-aging</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-blue-600 font-bold text-lg">•</span>
                  <span>Advanced laser therapy for pain relief and skin rejuvenation</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-blue-600 font-bold text-lg">•</span>
                  <span>IV nutrient therapy for optimal hydration and energy</span>
                </li>
              </ul>
            </div>

            {/* Column 2: Treatment Benefits */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl flex items-center justify-center mb-5 text-white text-2xl">
                💚
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Transformative Results</h3>
              <ul className="space-y-3">
                <li className="flex gap-3 text-gray-700">
                  <span className="text-teal-600 font-bold text-lg">•</span>
                  <span>Enhanced cellular regeneration and tissue repair</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-teal-600 font-bold text-lg">•</span>
                  <span>Accelerated muscle recovery and athletic performance</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-teal-600 font-bold text-lg">•</span>
                  <span>Improved immune function and natural detoxification</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-teal-600 font-bold text-lg">•</span>
                  <span>Visible anti-aging effects and increased vitality</span>
                </li>
              </ul>
            </div>

            {/* Column 3: Who Benefits */}
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-xl flex items-center justify-center mb-5 text-white text-2xl">
                🎯
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ideal For</h3>
              <ul className="space-y-3">
                <li className="flex gap-3 text-gray-700">
                  <span className="text-cyan-600 font-bold text-lg">•</span>
                  <span>Professional and amateur athletes seeking peak performance</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-cyan-600 font-bold text-lg">•</span>
                  <span>Those with chronic pain or injury recovery needs</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-cyan-600 font-bold text-lg">•</span>
                  <span>Anti-aging and longevity-focused individuals</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-cyan-600 font-bold text-lg">•</span>
                  <span>Anyone seeking comprehensive wellness optimization</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Two Column Details */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Personalized Treatment Plans</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our wellness practitioners create customized treatment protocols tailored to your specific health goals, fitness level, and wellness objectives. From initial assessment to ongoing progress monitoring, we ensure every treatment maximizes your results while maintaining complete safety and comfort.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Whether seeking single treatments or comprehensive wellness packages, our flexible options accommodate any lifestyle and budget while delivering premium therapeutic outcomes.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Luxury Wellness Experience</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Beyond therapeutic excellence, we provide a complete luxury wellness experience in serene, premium settings designed for complete relaxation and healing. Our state-of-the-art facilities, highly trained practitioners, and personalized attention ensure every session feels like an investment in your most valuable asset—your health.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Transform your wellness journey through cutting-edge treatments that deliver visible, measurable results and lasting health improvements.
              </p>
            </div>
          </div>
        </div>
      </section>

        {/* Why Choose Us */}
        <div className="bg-white rounded-2xl shadow-lg p-12 mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Why Choose Our Wellness Treatments?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Expert Practitioners</h3>
              <p className="text-gray-600">Certified wellness experts with years of experience in advanced treatments</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Cutting-Edge Technology</h3>
              <p className="text-gray-600">State-of-the-art equipment and innovative treatment methodologies</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Personalized Care</h3>
              <p className="text-gray-600">Tailored treatments designed specifically for your unique wellness needs</p>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Ready to Transform Your Wellness?</h2>
          <p className="text-gray-600 mb-8">Book a consultation with our wellness experts and discover the perfect treatment for you.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="border-2 border-teal-600 text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-teal-600 hover:text-white transition-colors flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" />
              Call +919982498555
            </button>
          </div>
        </div>
      </div>

      {/* Treatment Modal */}
      <TreatmentModal 
        treatment={selectedTreatment} 
        onClose={() => setSelectedTreatment(null)} 
      />
    </div>
  );
};

export default WellnessTreatments;